// @file Composable for whiteboard page handlers
import { captureException } from '@@/bits/error_tracker'
import type { PageMeta } from '@@/bits/whiteboard'
import { findNearestFrame } from '@@/bits/whiteboard'
import { canViewerSeeFrame, getVisibilityFromPageMeta } from '@@/bits/whiteboard_visibility'
import { PostVisibility } from '@@/enums'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { cloneDeep } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import type { Editor, TLPage, TLPageId } from 'tldraw'

const useWhiteboardPageHandlers = (
  editor: Editor,
): {
  updatePageAttribution: (page: TLPage) => TLPage
  registerSideEffects: () => void
} => {
  const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())
  const whiteboardStore = useWhiteboardStore()

  // #region Helpers
  const isPageAttributionPopulated = (meta: TLPage['meta']): boolean => {
    return meta.lastModifiedBy != null && meta.lastModifiedAt != null
  }
  // #endregion

  // #region PAGE (frame) SIDE EFFECTS
  const updatePageAttribution = (page: TLPage): TLPage => {
    const clonedPage = cloneDeep(page)
    if (page === undefined || clonedPage === undefined) {
      captureException(new Error('Page is undefined on before change'), { tags: { isTldrawError: true } })
      return page
    }
    clonedPage.meta = clonedPage.meta ?? {}
    clonedPage.meta.lastModifiedAt = Date.now()
    clonedPage.meta.lastModifiedBy = currentUser.value.id
    return clonedPage
  }

  const registerSideEffects = (): void => {
    editor.sideEffects.registerBeforeCreateHandler('page', (page, source) => {
      if (source !== 'user') return page

      // Assign attribution when page is created. Page attribution can already be populated
      // if the page is duplicated (in which case we keep its original meta).
      if (!isPageAttributionPopulated(page.meta)) {
        const clonedPage = cloneDeep(page)
        clonedPage.meta = {
          // Page meta can have other attributies like `backgroundType`. We need to keep them.
          ...clonedPage.meta,
          createdBy: currentUser.value.id,
          lastModifiedAt: Date.now(),
          lastModifiedBy: currentUser.value.id,
          useThemeFrameBackground: true,
        }
        return clonedPage
      }

      return page
    })

    editor.sideEffects.registerBeforeChangeHandler('page', (_prev, next, source) => {
      if (source !== 'user') return next
      return updatePageAttribution(next)
    })

    // When a page becomes restricted (visibility narrows from "everyone"), move the current
    // user off of it if they can no longer see it. Uses the sideEffects hook rather
    // than a reactive effect so (a) it covers remote changes from collaborators' toggles
    // via sync, and (b) it doesn't fire on initial mount — sandbox exports relied on
    // the initial-fire of a reactive fallback, which raced with URL-driven navigation
    // and caused every screenshot to capture the same frame.
    editor.sideEffects.registerAfterChangeHandler('page', (prev, next) => {
      const wasRestricted = getVisibilityFromPageMeta(prev.meta as PageMeta) !== PostVisibility.Everyone
      const isRestricted = getVisibilityFromPageMeta(next.meta as PageMeta) !== PostVisibility.Everyone
      if (wasRestricted || !isRestricted) return
      if (editor.getCurrentPageId() !== next.id) return

      // Evaluate against `next.meta` (the pending change), but reuse the store's canonical context so
      // this gate can't drift from canISeeFrame / the navbar filter.
      if (canViewerSeeFrame(next.meta as PageMeta, whiteboardStore.buildFrameViewerContext())) return

      // Exclude next.id from the frame list: whiteboardStore.frames is updated by a
      // reactive effect that runs in the microtask after this synchronous afterChange
      // handler, so at this point it may still list the just-toggled page as visible.
      const availableFrames = whiteboardStore.frames.filter((f) => f.id !== next.id)
      const nearest = findNearestFrame(editor.getPages(), availableFrames, next.id)
      if (nearest != null) {
        editor.setCurrentPage(nearest.id as TLPageId)
      }
    })
  }
  // #endregion

  return { updatePageAttribution, registerSideEffects }
}
export { useWhiteboardPageHandlers }
